/**
 * THE SUGGESTED FIGURES FOR AN INVESTMENT PROPERTY BOUGHT IN A PLAN (village rebuild, owner 21 Oct 2026).
 *
 * One source for the engine's tested plans and the "Build a plan yourself" set-up, so the card and the
 * set-up can never start from different figures:
 *   - rent: the firm's rental yield on the price (3.5% unless the firm sets its own);
 *   - two weeks a year without a tenant, when the rent stops and the costs go on;
 *   - the agent's management fee, 8% of the rent collected;
 *   - repairs and upkeep, 1% of the value a year.
 * Council rates, insurance, land tax and strata are the client's to enter — land tax turns on the land
 * value, the state's threshold and other land held, so no figure is guessed. Each is "suggested" on
 * screen until it is changed.
 */
import { ASSET_CLASS_ASSUMPTIONS } from '../investmentProfiles.ts';

export const SUGGESTED_PROPERTY_VACANCY_WEEKS = 2;
export const SUGGESTED_AGENT_FEE_PCT = 8;
export const SUGGESTED_UPKEEP_PCT = 1;

/** The costs in words, for every surface that states the property's assumptions. */
export const SUGGESTED_PROPERTY_COSTS_WORDS = `two weeks a year without a tenant, the agent's ${SUGGESTED_AGENT_FEE_PCT}% of the rent and ${SUGGESTED_UPKEEP_PCT}% of the value a year for upkeep (rates, insurance and land tax only where entered)`;

export interface PropertyFigures {
    /** The rent a year if it were let every week. */
    grossRent: number;
    vacancyWeeks: number;
    /** The rent a year after the weeks it sits empty. */
    rent: number;
    agentFee: number;
    upkeep: number;
    /** Rates, insurance, land tax and strata, as entered (0 until then). */
    otherCosts: number;
    /** Every cost a year: the agent, upkeep and the entered costs. */
    expenses: number;
}

export function suggestedPropertyFigures(
    price: number,
    over: { weeklyRent?: number | null; vacancyWeeks?: number | null; agentFeePct?: number | null; upkeep?: number | null; otherCosts?: number | null; yieldPct?: number } = {},
): PropertyFigures {
    const p = Math.max(0, Number(price) || 0);
    const yieldPct = over.yieldPct ?? ASSET_CLASS_ASSUMPTIONS.propertyYieldPct;
    const grossRent = over.weeklyRent != null ? Math.max(0, over.weeklyRent) * 52 : p * (yieldPct / 100);
    const vacancyWeeks = Math.min(52, Math.max(0, over.vacancyWeeks ?? SUGGESTED_PROPERTY_VACANCY_WEEKS));
    const rent = grossRent * (52 - vacancyWeeks) / 52;
    const agentFee = rent * ((over.agentFeePct ?? SUGGESTED_AGENT_FEE_PCT) / 100);
    const upkeep = over.upkeep != null ? Math.max(0, over.upkeep) : p * (SUGGESTED_UPKEEP_PCT / 100);
    const otherCosts = Math.max(0, over.otherCosts ?? 0);
    const r = (x: number) => Math.round(x);
    return { grossRent: r(grossRent), vacancyWeeks, rent: r(rent), agentFee: r(agentFee), upkeep: r(upkeep), otherCosts: r(otherCosts), expenses: r(agentFee + upkeep + otherCosts) };
}
